import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PortalLayout from '../PortalLayout';
import { payrunService, payslipService, employeeV2Service } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';
import { C } from '../../../styles/theme';

// ─── Tokens ──────────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (currency, amount) => `${currency}${Number(amount || 0).toFixed(2)}`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtPeriod = (start, end) => `${fmtDate(start)} – ${fmtDate(end)}`;

const STATUS_STYLES = {
    draft: { color: C.slate, bg: C.slateLight },
    ready: { color: C.amber, bg: C.amberLight },
    partially_paid: { color: C.amber, bg: C.amberLight },
    paid: { color: C.blue, bg: C.blueLight },
    closed: { color: C.green, bg: C.greenLight },
};

const CADENCE_LABEL_KEYS = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' };
const STATUS_LABEL_KEYS = { draft: 'status_draft', ready: 'status_ready', partially_paid: 'status_partially_paid', paid: 'status_paid', closed: 'status_closed' };

const sumPaid = (line) =>
    (line.payments || []).reduce((acc, p) => acc + Number(p.amount || 0), 0);

const runTotals = (run) => {
    const gross = Number(run?.total_gross || 0);
    const net = Number(run?.total_net || 0);
    const paid = Number(run?.total_paid || 0);
    const deductions = gross - net;
    const owed = Math.max(0, net - paid);
    return { gross, deductions, net, paid, owed };
};

// ─── StatusPill ──────────────────────────────────────────────────────────────

const StatusPill = ({ status, t }) => {
    const style = STATUS_STYLES[status] || { color: C.muted, bg: C.slateLight };
    return (
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '3px 9px', background: style.bg, color: style.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t(STATUS_LABEL_KEYS[status] || status)}
        </span>
    );
};

// ─── Summary Strip ───────────────────────────────────────────────────────────

const SummaryCell = ({ label, value, emphasize = false }) => (
    <div style={{ flex: 1, padding: '16px 20px', borderRight: `1px solid ${C.borderLight}` }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: emphasize ? 20 : 17, fontWeight: emphasize ? 800 : 700, color: emphasize ? C.green : C.text }}>{value}</div>
    </div>
);

const SummaryStrip = ({ totals, currency, t }) => (
    <div style={{ display: 'flex', background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
        <SummaryCell label={t('gross')} value={fmtMoney(currency, totals.gross)} />
        <SummaryCell label={t('deductions')} value={fmtMoney(currency, totals.deductions)} />
        <SummaryCell label={t('net')} value={fmtMoney(currency, totals.net)} emphasize />
        <SummaryCell label={t('paid')} value={fmtMoney(currency, totals.paid)} />
        <div style={{ flex: 1, padding: '16px 20px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('owed')}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: totals.owed > 0 ? C.amber : C.green }}>{fmtMoney(currency, totals.owed)}</div>
        </div>
    </div>
);

// ─── Confirm Modal ───────────────────────────────────────────────────────────

const ConfirmModal = ({ title, message, confirmLabel, danger, loading, onConfirm, onClose }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 420, overflow: 'hidden' }}
            onKeyDown={e => e.key === 'Escape' && onClose()}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{title}</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
            <div style={{ padding: '20px 24px', fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{message}</div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {onClose.label || 'Cancel'}
                </button>
                <button onClick={onConfirm} disabled={loading}
                    style={{ background: danger ? C.red : C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit' }}>
                    {loading ? '…' : confirmLabel}
                </button>
            </div>
        </div>
    </div>
);

// ─── Reopen Modal (requires reason) ─────────────────────────────────────────

const ReopenModal = ({ loading, onConfirm, onClose, t }) => {
    const [reason, setReason] = useState('');
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, overflow: 'hidden' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('reopen_run')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 13, color: C.textLight }}>{t('reopen_run_message')}</div>
                    <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder={t('reason')}
                        style={{ width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={() => onConfirm(reason)} disabled={loading || !reason.trim()}
                        style={{ background: C.amber, color: C.white, border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: (loading || !reason.trim()) ? 'default' : 'pointer', opacity: (loading || !reason.trim()) ? 0.7 : 1, fontFamily: 'inherit' }}>
                        {loading ? '…' : t('reopen')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Sticky Header ───────────────────────────────────────────────────────────

const headerBtnStyle = (bg, color) => ({
    display: 'flex', alignItems: 'center', gap: 6, background: bg, color, border: 'none',
    borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
});

const StickyHeader = ({ run, onBack, onAction, actionLoading, t }) => (
    <div style={{ position: 'sticky', top: 0, zIndex: 10, background: C.white, borderBottom: `1px solid ${C.border}`, padding: '14px 24px', marginLeft: -24, marginRight: -24, marginTop: -24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button onClick={onBack}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '6px 10px', borderRadius: 6, fontFamily: 'inherit' }}
                onMouseEnter={e => { e.currentTarget.style.background = C.bg; e.currentTarget.style.color = C.text; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.muted; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                </svg>
                {t('back')}
            </button>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, padding: '3px 9px', background: C.slateLight, borderRadius: 5 }}>
                    {t(CADENCE_LABEL_KEYS[run.cadence] || run.cadence)}
                </span>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>
                    {fmtPeriod(run.period_start, run.period_end)}
                </h2>
            </div>
            <StatusPill status={run.status} t={t} />
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: C.muted, marginRight: 8 }}>
                    <strong style={{ color: C.textLight, fontWeight: 700 }}>{t('cutoff')}:</strong> {fmtDate(run.period_end)}
                    <span style={{ margin: '0 8px', color: C.borderLight }}>|</span>
                    <strong style={{ color: C.textLight, fontWeight: 700 }}>{t('pay_date')}:</strong> {fmtDate(run.pay_date)}
                </span>
                {run.status === 'draft' && (
                    <button onClick={() => onAction('delete')} disabled={actionLoading} style={headerBtnStyle(C.redLight, C.red)}>
                        {t('delete')}
                    </button>
                )}
                {run.status === 'draft' && (
                    <>
                        <button onClick={() => onAction('refresh')} disabled={actionLoading} style={headerBtnStyle(C.white, C.textLight)} onMouseEnter={e => e.currentTarget.style.background = C.bg} onMouseLeave={e => e.currentTarget.style.background = C.white}>
                            {t('refresh_draft')}
                        </button>
                        <button onClick={() => onAction('finalize')} disabled={actionLoading} style={headerBtnStyle(C.green, C.white)}>
                            {t('finalize')}
                        </button>
                    </>
                )}
                {(run.status === 'ready' || run.status === 'partially_paid') && (
                    <button onClick={() => onAction('re_finalize')} disabled={actionLoading} style={headerBtnStyle(C.slateLight, C.slate)}>
                        {t('re_finalize')}
                    </button>
                )}
                {run.status === 'paid' && (
                    <button onClick={() => onAction('close')} disabled={actionLoading} style={headerBtnStyle(C.green, C.white)}>
                        {t('close_run')}
                    </button>
                )}
                {run.status === 'closed' && (
                    <button onClick={() => onAction('reopen')} disabled={actionLoading} style={headerBtnStyle(C.amberLight, C.amber)}>
                        {t('reopen')}
                    </button>
                )}
            </div>
        </div>
    </div>
);

// ─── Line Status Pill ────────────────────────────────────────────────────────

const LINE_STATUS_STYLES = {
    unpaid:  { color: C.slate, bg: C.slateLight },
    partial: { color: C.amber, bg: C.amberLight },
    settled: { color: C.green, bg: C.greenLight },
};

const LineStatusPill = ({ status, t }) => {
    const s = LINE_STATUS_STYLES[status] || LINE_STATUS_STYLES.unpaid;
    return (
        <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 7px', background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t(status)}
        </span>
    );
};

// ─── Line Item Grid ─────────────────────────────────────────────────────────

const COLS = [
    { key: 'employee',   labelKey: 'employee',     align: 'left',  flex: '2 1 180px' },
    { key: 'base',       labelKey: 'base',          align: 'right', flex: '1 1 100px' },
    { key: 'deductions', labelKey: 'deductions',    align: 'right', flex: '1 1 100px' },
    { key: 'net',        labelKey: 'net',           align: 'right', flex: '1 1 100px' },
    { key: 'owed',       labelKey: 'owed',          align: 'right', flex: '1 1 100px' },
    { key: 'status',     labelKey: 'status',        align: 'center', flex: '0 0 90px' },
    { key: 'actions',    labelKey: '',              align: 'center', flex: '0 0 44px' },
];

const lineDeductions = (li) => Number(li.advance_deduction || 0) + Number(li.allowance_deduction || 0) + Number(li.other_deductions || 0);

const LINE_STATUS_TONE = { unpaid: 'gray', partial: 'amber', settled: 'green' };

const MobileLineItemRow = ({ li, name, currency, canPay, isClosed, onEmployeeClick, onPayNow, onViewPayslip, t }) => {
    const paidAmt = sumPaid(li);
    const owed = Math.max(0, Number(li.net_pay || 0) - paidAmt);
    const negNet = Number(li.net_pay || 0) < 0;
    const ded = lineDeductions(li);
    const detailRow = (label, value, color) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: C.muted }}>{label}</span>
            <span style={{ fontWeight: 600, color: color || C.text, textAlign: 'right' }}>{value}</span>
        </div>
    );
    return (
        <MobileCard
            accent={negNet && !li.acknowledged_negative_net ? 'red' : undefined}
            title={name}
            amount={fmtMoney(currency, li.net_pay)}
            status={{ label: t(li.status), tone: LINE_STATUS_TONE[li.status] || 'gray' }}
            subtitle={`${t('base')} ${fmtMoney(currency, li.base_pay)}${ded > 0 ? ` · -${fmtMoney(currency, ded)} ${t('deductions')}` : ''}`}
        >
            {detailRow(t('base'), fmtMoney(currency, li.base_pay))}
            {detailRow(t('deductions'), ded > 0 ? `-${fmtMoney(currency, ded)}` : fmtMoney(currency, 0), ded > 0 ? C.red : C.text)}
            {detailRow(t('net'), fmtMoney(currency, li.net_pay), negNet ? C.red : C.text)}
            {detailRow(t('owed'), fmtMoney(currency, owed), owed > 0 ? C.amber : C.muted)}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button onClick={() => onEmployeeClick(li.employee_id)}
                    style={{ flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {t('view_employee') || 'View Employee'}
                </button>
                {canPay && li.status !== 'settled' && (
                    <button onClick={() => onPayNow(li)}
                        style={{ flex: 1, background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('record_payment')}
                    </button>
                )}
                {isClosed && li.status === 'settled' && (
                    <button onClick={() => onViewPayslip(li)}
                        style={{ flex: 1, background: C.blueLight, color: C.blue, border: 'none', borderRadius: 8, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('view_payslip')}
                    </button>
                )}
            </div>
        </MobileCard>
    );
};

const LineItemGrid = ({ lineItems, employeeMap, currency, runStatus, onEmployeeClick, onPayNow, onViewPayslip, t, isMobile }) => {
    const canPay = runStatus === 'ready' || runStatus === 'partially_paid' || runStatus === 'paid';
    const isClosed = runStatus === 'closed';
    if (!lineItems.length) {
        return (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 48, textAlign: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" style={{ marginBottom: 10, opacity: 0.5 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.textLight }}>{t('no_line_items')}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{t('no_line_items_hint')}</div>
            </div>
        );
    }

    if (isMobile) {
        return (
            <div>
                {lineItems.map(li => {
                    const emp = employeeMap[li.employee_id];
                    const name = emp ? (emp.name || li.employee_id) : li.employee_id;
                    return (
                        <MobileLineItemRow
                            key={li.line_item_id}
                            li={li}
                            name={name}
                            currency={currency}
                            canPay={canPay}
                            isClosed={isClosed}
                            onEmployeeClick={onEmployeeClick}
                            onPayNow={onPayNow}
                            onViewPayslip={onViewPayslip}
                            t={t}
                        />
                    );
                })}
            </div>
        );
    }

    return (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${C.borderLight}`, padding: '0 8px' }}>
                {COLS.map(col => (
                    <div key={col.key} style={{ flex: col.flex, padding: '11px 10px', fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, textAlign: col.align, whiteSpace: 'nowrap' }}>
                        {t(col.labelKey)}
                    </div>
                ))}
            </div>
            {/* Rows */}
            {lineItems.map(li => {
                const emp = employeeMap[li.employee_id];
                const name = emp ? (emp.name || li.employee_id) : li.employee_id;
                const paidAmt = sumPaid(li);
                const owed = Math.max(0, Number(li.net_pay || 0) - paidAmt);
                const negNet = Number(li.net_pay || 0) < 0;

                return (
                    <div key={li.line_item_id}
                        style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.bg}`, padding: '0 8px', transition: 'background 0.1s', cursor: 'default' }}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        {/* Employee */}
                        <div style={{ flex: COLS[0].flex, padding: '10px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            {negNet && !li.acknowledged_negative_net && (
                                <span title={t('negative_net_warning')} style={{ color: C.red, fontSize: 14, lineHeight: 1 }}>⚠</span>
                            )}
                            <button onClick={() => onEmployeeClick(li.employee_id)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: C.green, textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                                {name}
                            </button>
                        </div>
                        {/* Base */}
                        <div style={{ flex: COLS[1].flex, padding: '10px 10px', fontSize: 13, color: C.textLight, textAlign: 'right' }}>{fmtMoney(currency, li.base_pay)}</div>
                        {/* Deductions */}
                        <div style={{ flex: COLS[2].flex, padding: '10px 10px', fontSize: 13, color: lineDeductions(li) > 0 ? C.red : C.textLight, textAlign: 'right' }}>
                            {lineDeductions(li) > 0 ? `-${fmtMoney(currency, lineDeductions(li))}` : fmtMoney(currency, 0)}
                        </div>
                        {/* Net */}
                        <div style={{ flex: COLS[3].flex, padding: '10px 10px', fontSize: 13, fontWeight: 700, color: negNet ? C.red : C.text, textAlign: 'right' }}>{fmtMoney(currency, li.net_pay)}</div>
                        {/* Owed */}
                        <div style={{ flex: COLS[4].flex, padding: '10px 10px', fontSize: 13, fontWeight: 600, color: owed > 0 ? C.amber : C.muted, textAlign: 'right' }}>{fmtMoney(currency, owed)}</div>
                        {/* Status */}
                        <div style={{ flex: COLS[5].flex, padding: '10px 10px', textAlign: 'center' }}>
                            <LineStatusPill status={li.status} t={t} />
                        </div>
                        {/* Actions */}
                        <div style={{ flex: COLS[6].flex, padding: '10px 4px', textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                            {canPay && li.status !== 'settled' && (
                                <button onClick={() => onPayNow(li)} title={t('record_payment')}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, borderRadius: 4, display: 'inline-flex' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = C.greenLight; e.currentTarget.style.color = C.green; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.mutedLight; }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                    </svg>
                                </button>
                            )}
                            {isClosed && li.status === 'settled' && (
                                <button onClick={() => onViewPayslip(li)} title={t('view_payslip')}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, borderRadius: 4, display: 'inline-flex' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = C.blueLight; e.currentTarget.style.color = C.blue; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.mutedLight; }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Pay Now Modal ──────────────────────────────────────────────────────────

const PAYMENT_METHODS = ['cash', 'bank_transfer', 'cash_from_till'];

const PayNowModal = ({ lineItem, employeeName, currency, loading, onSave, onClose, t }) => {
    const owed = Math.max(0, Number(lineItem.net_pay || 0) - sumPaid(lineItem));
    const [form, setForm] = useState({ amount: owed.toFixed(2), method: 'bank_transfer', notes: '' });
    const [error, setError] = useState('');

    const handleSubmit = () => {
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) return setError(t('amount_required'));
        setError('');
        onSave({
            amount: amt,
            method: form.method,
            notes: form.notes || undefined,
        });
    };

    const inputSt = { width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
    const lblSt = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, overflow: 'hidden' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('record_payment')}</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{employeeName}</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>{error}</div>}
                    <div style={{ display: 'flex', gap: 16, background: C.bg, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.muted }}>
                        <span>{t('net')}: <strong style={{ color: C.text }}>{fmtMoney(currency, lineItem.net_pay)}</strong></span>
                        <span>{t('paid')}: <strong style={{ color: C.green }}>{fmtMoney(currency, sumPaid(lineItem))}</strong></span>
                        <span>{t('owed')}: <strong style={{ color: owed > 0 ? C.amber : C.green }}>{fmtMoney(currency, owed)}</strong></span>
                    </div>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={lblSt}>{t('amount')}</label>
                            <input style={inputSt} type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                        </div>
                        <div>
                            <label style={lblSt}>{t('method')}</label>
                            <select style={inputSt} value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
                                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{t(m)}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label style={lblSt}>{t('notes')} <span style={{ color: C.mutedLight, fontWeight: 500 }}>({t('optional')})</span></label>
                        <input style={inputSt} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                </div>
                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSubmit} disabled={loading}
                        style={{ background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit' }}>
                        {loading ? '…' : t('record_payment')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Bulk Payment Modal ─────────────────────────────────────────────────────

const BulkPaymentModal = ({ lineItems, employeeMap, currency, loading, onConfirm, onClose, t }) => {
    const unpaid = lineItems.filter(li => li.status !== 'settled');
    const initial = {};
    unpaid.forEach(li => { initial[li.line_item_id] = Math.max(0, Number(li.net_pay || 0) - sumPaid(li)); });
    const [amounts, setAmounts] = useState(initial);
    const [method, setMethod] = useState('bank_transfer');

    const setAmt = (id, val) => setAmounts(prev => ({ ...prev, [id]: parseFloat(val) || 0 }));
    const totalToPay = Object.values(amounts).reduce((a, b) => a + b, 0);

    const handleSubmit = () => {
        const payments = Object.entries(amounts)
            .filter(([, amt]) => amt > 0)
            .map(([line_item_id, amount]) => ({ line_item_id, amount, method }));
        if (payments.length) onConfirm(payments);
    };

    const inputSt = { width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                {/* Header */}
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('record_all_unpaid')}</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{unpaid.length} {t('employees')}</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                {/* Method selector */}
                <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.borderLight}`, flexShrink: 0 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 }}>{t('payment_method')}</label>
                    <select style={{ ...inputSt, width: 200 }} value={method} onChange={e => setMethod(e.target.value)}>
                        {PAYMENT_METHODS.map(m => <option key={m} value={m}>{t(m)}</option>)}
                    </select>
                </div>
                {/* Employee list */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
                    {unpaid.map(li => {
                        const emp = employeeMap[li.employee_id];
                        const name = emp ? (emp.name || li.employee_id) : li.employee_id;
                        const owed = Math.max(0, Number(li.net_pay || 0) - sumPaid(li));
                        return (
                            <div key={li.line_item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${C.bg}` }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{name}</div>
                                    <div style={{ fontSize: 11, color: C.muted }}>{t('owed')}: {fmtMoney(currency, owed)}</div>
                                </div>
                                <input type="number" step="0.01" min="0"
                                    value={amounts[li.line_item_id] ?? 0}
                                    onChange={e => setAmt(li.line_item_id, e.target.value)}
                                    style={{ ...inputSt, width: 120, textAlign: 'right' }} />
                            </div>
                        );
                    })}
                </div>
                {/* Footer */}
                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                        {t('total')}: <span style={{ color: C.green }}>{fmtMoney(currency, totalToPay)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {t('cancel')}
                        </button>
                        <button onClick={handleSubmit} disabled={loading || totalToPay <= 0}
                            style={{ background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: (loading || totalToPay <= 0) ? 'default' : 'pointer', opacity: (loading || totalToPay <= 0) ? 0.7 : 1, fontFamily: 'inherit' }}>
                            {loading ? t('processing') : t('confirm_payments')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Payslip Print ──────────────────────────────────────────────────────────

const buildPayslipHtml = (snap, t) => {
    const cur = (v) => Number(v || 0).toFixed(2);
    const d = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
    const emp = snap.employee || {};
    const pr = snap.pay_run || {};
    const earn = snap.earnings || {};
    const ded = snap.deductions || {};
    const tot = snap.totals || {};
    const pmts = snap.payments || [];

    const earningRows = [
        ['Base Pay', cur(earn.base_pay)],
        ['Overtime', cur(earn.overtime_pay)],
        ['Tips', cur(earn.tips_pay)],
        ['Bonus', cur(earn.bonus_pay)],
        ['Back Pay', cur(earn.back_pay)],
    ].filter(([, v]) => parseFloat(v) > 0);

    const deductionRows = [
        ['Advance', cur(ded.advance_deduction)],
        ['Allowance', cur(ded.allowance_deduction)],
        ['Other', cur(ded.other_deductions)],
    ].filter(([, v]) => parseFloat(v) > 0);

    const paymentRows = pmts.map(p => `<tr><td>${d(p.payment_date)}</td><td>${p.method}</td><td style="text-align:right">${cur(p.amount)}</td></tr>`).join('');

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payslip</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Plus Jakarta Sans',system-ui,sans-serif;color:#212529;padding:40px;max-width:700px;margin:auto;font-size:13px}
h1{font-size:20px;font-weight:800;margin-bottom:4px}
.sub{color:#64748b;font-size:12px;margin-bottom:24px}
.section{margin-bottom:20px}
.section-title{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:#94a3b8;margin-bottom:8px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
table{width:100%;border-collapse:collapse}
td,th{padding:6px 8px;text-align:left;border-bottom:1px solid #f1f5f9}
.right{text-align:right}
.total-row{font-weight:700;border-top:2px solid #212529}
.total-row.green{color:#2563eb}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin-bottom:20px}
.info-grid .label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:#94a3b8}
.info-grid .value{font-size:13px;font-weight:600;color:#212529}
@media print{body{padding:20px}}
</style></head><body>
<h1>${t('payslip')}</h1>
<div class="sub">${snap.tenant?.name || ''} · ${d(pr.period_start)} – ${d(pr.period_end)}</div>
<div class="info-grid">
<div><div class="label">${t('employee')}</div><div class="value">${emp.name || emp.employee_id || '—'}</div></div>
<div><div class="label">${t('role')}</div><div class="value">${emp.role || '—'}</div></div>
<div><div class="label">${t('pay_date')}</div><div class="value">${d(pr.pay_date)}</div></div>
<div><div class="label">${t('cadence')}</div><div class="value">${pr.cadence || '—'}</div></div>
</div>
<div class="section"><div class="section-title">${t('earnings')}</div><table>
${earningRows.map(([l, v]) => `<tr><td>${l}</td><td class="right">${v}</td></tr>`).join('')}
<tr class="total-row"><td>${t('gross')}</td><td class="right">${cur(tot.gross_pay)}</td></tr>
</table></div>
${deductionRows.length ? `<div class="section"><div class="section-title">${t('deductions')}</div><table>
${deductionRows.map(([l, v]) => `<tr><td>${l}</td><td class="right">-${v}</td></tr>`).join('')}
</table></div>` : ''}
<div class="section"><table>
<tr class="total-row green"><td>${t('net')}</td><td class="right">${cur(tot.net_pay)}</td></tr>
</table></div>
${pmts.length ? `<div class="section"><div class="section-title">${t('payments')}</div><table>
<tr><th>${t('date')}</th><th>${t('method')}</th><th class="right">${t('amount')}</th></tr>
${paymentRows}
<tr class="total-row"><td colspan="2">${t('total_paid')}</td><td class="right">${cur(tot.paid)}</td></tr>
</table></div>` : ''}
<script>window.onload=function(){window.print()}</script>
</body></html>`;
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const PortalPayRunDetailPage = () => {
    const navigate = useNavigate();
    const { payRunId } = useParams();
    const { getCurrency } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const currency = getCurrency();

    const [run, setRun] = useState(null);
    const [employeeMap, setEmployeeMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [actionLoading, setActionLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const [showReopen, setShowReopen] = useState(false);
    const [payNowLine, setPayNowLine] = useState(null);
    const [payLoading, setPayLoading] = useState(false);
    const [showBulkPay, setShowBulkPay] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const [runData, employees] = await Promise.all([
                payrunService.get(payRunId),
                employeeV2Service.listEmployees(),
            ]);
            setRun(runData);
            const map = {};
            (employees || []).forEach(e => { map[e.employee_id] = e; });
            setEmployeeMap(map);
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_load_pay_run'));
        } finally {
            setLoading(false);
        }
    }, [payRunId, t]);

    useEffect(() => { load(); }, [load]);

    const handleAction = (action) => {
        if (action === 'reopen') { setShowReopen(true); return; }
        if (action === 'delete') {
            setConfirmModal({
                title: t('delete_run'), message: t('delete_run_message'), confirmLabel: t('delete'), danger: true,
                fn: async () => { await payrunService.deleteDraft(payRunId); navigate('/portal/payrun'); return null; },
            });
            return;
        }
        const configs = {
            refresh:      { title: t('refresh_draft'), message: t('refresh_draft_message'), confirmLabel: t('refresh'), danger: false, fn: () => payrunService.refreshDraft(payRunId) },
            finalize:     { title: t('finalize_run'), message: t('finalize_run_message'), confirmLabel: t('finalize'), danger: false, fn: () => payrunService.finalize(payRunId) },
            re_finalize:  { title: t('re_finalize_run'), message: t('re_finalize_message'), confirmLabel: t('re_finalize'), danger: false, fn: () => payrunService.reFinalize(payRunId) },
            close:        { title: t('close_run'), message: t('close_run_message'), confirmLabel: t('close_run'), danger: true, fn: () => payrunService.close(payRunId) },
        };
        setConfirmModal(configs[action] || null);
    };

    const execConfirm = async () => {
        if (!confirmModal) return;
        try {
            setActionLoading(true);
            const updated = await confirmModal.fn();
            if (updated) setRun(updated);
            setConfirmModal(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('action_failed'));
            setConfirmModal(null);
        } finally { setActionLoading(false); }
    };

    const execReopen = async (reason) => {
        try {
            setActionLoading(true);
            const updated = await payrunService.reopen(payRunId, reason);
            setRun(updated);
            setShowReopen(false);
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed'));
        } finally { setActionLoading(false); }
    };

    const execPayNow = async (payload) => {
        if (!payNowLine) return;
        try {
            setPayLoading(true);
            await payrunService.recordSinglePayment(payRunId, payNowLine.line_item_id, payload);
            setPayNowLine(null);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('action_failed'));
        } finally { setPayLoading(false); }
    };

    const execBulkPay = async (payments) => {
        if (!payments?.length) return;
        try {
            setBulkLoading(true);
            await payrunService.recordPayments(payRunId, payments);
            setShowBulkPay(false);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed'));
        } finally { setBulkLoading(false); }
    };

    const viewPayslip = async (li) => {
        const payslips = li.payslips || [];
        if (!payslips.length) { setError(t('no_payslip_found')); return; }
        try {
            const data = await payslipService.getPdfPayload(payslips[0].payslip_id);
            const html = buildPayslipHtml(data.snapshot_json, t);
            const win = window.open('', '_blank');
            if (win) { win.document.write(html); win.document.close(); }
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed'));
        }
    };

    if (loading) {
        return (
            <PortalLayout>
                <div style={{ fontFamily: FONT, padding: 24, color: C.mutedLight, fontSize: 13 }}>{t('loading')}</div>
            </PortalLayout>
        );
    }

    if (error || !run) {
        return (
            <PortalLayout>
                <div style={{ fontFamily: FONT, padding: 24 }}>
                    <button onClick={() => navigate('/portal/payrun')}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '6px 10px', borderRadius: 6, fontFamily: 'inherit', marginBottom: 16 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        {t('back')}
                    </button>
                    <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#e11d48' }}>
                        {error || t('pay_run_not_found')}
                    </div>
                </div>
            </PortalLayout>
        );
    }

    const totals = runTotals(run);
    const lineItems = run.line_items || [];

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT }}>
                <StickyHeader run={run} onBack={() => navigate('/portal/payrun')} onAction={handleAction} actionLoading={actionLoading} t={t} />
                {isMobile ? (
                    <MobileHero
                        label={t('net')}
                        value={fmtMoney(currency, totals.net)}
                        meta={`${t(CADENCE_LABEL_KEYS[run.cadence] || run.cadence)} · ${fmtPeriod(run.period_start, run.period_end)}`}
                        stats={[
                            { label: t('gross'), value: fmtMoney(currency, totals.gross) },
                            { label: t('paid'), value: fmtMoney(currency, totals.paid), tone: 'green' },
                            { label: t('owed'), value: fmtMoney(currency, totals.owed), tone: totals.owed > 0 ? 'red' : undefined },
                        ]}
                    />
                ) : (
                    <SummaryStrip totals={totals} currency={currency} t={t} />
                )}

                <LineItemGrid
                    lineItems={lineItems}
                    employeeMap={employeeMap}
                    currency={currency}
                    runStatus={run.status}
                    onEmployeeClick={(empId) => navigate(`/portal/employees/${empId}`)}
                    onPayNow={(li) => setPayNowLine(li)}
                    onViewPayslip={viewPayslip}
                    t={t}
                    isMobile={isMobile}
                />

                {/* Bottom bar */}
                {lineItems.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, padding: '12px 16px', background: C.white, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {(run.status === 'ready' || run.status === 'partially_paid' || run.status === 'paid') && totals.owed > 0 && (
                                <button onClick={() => setShowBulkPay(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                    </svg>
                                    {`${t('record_all_unpaid')} (${fmtMoney(currency, totals.owed)})`}
                                </button>
                            )}
                        </div>
                        <button onClick={async () => {
                                try {
                                    const token = localStorage.getItem('token');
                                    const res = await fetch(payrunService.exportCsvUrl(payRunId), { headers: { Authorization: `Bearer ${token}` } });
                                    if (!res.ok) throw new Error();
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = `payrun-${payRunId}.csv`; a.click();
                                    URL.revokeObjectURL(url);
                                } catch { setError(t('action_failed')); }
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = C.white}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            {t('export_csv')}
                        </button>
                    </div>
                )}
            </div>

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    loading={actionLoading}
                    onConfirm={execConfirm}
                    onClose={() => setConfirmModal(null)}
                />
            )}
            {showReopen && (
                <ReopenModal loading={actionLoading} onConfirm={execReopen} onClose={() => setShowReopen(false)} t={t} />
            )}
            {showBulkPay && (
                <BulkPaymentModal
                    lineItems={lineItems}
                    employeeMap={employeeMap}
                    currency={currency}
                    loading={bulkLoading}
                    onConfirm={execBulkPay}
                    onClose={() => setShowBulkPay(false)}
                    t={t}
                />
            )}
            {payNowLine && (
                <PayNowModal
                    lineItem={payNowLine}
                    employeeName={(() => { const e = employeeMap[payNowLine.employee_id]; return e ? (e.name || payNowLine.employee_id) : payNowLine.employee_id; })()}
                    currency={currency}
                    loading={payLoading}
                    onSave={execPayNow}
                    onClose={() => setPayNowLine(null)}
                    t={t}
                />
            )}
        </PortalLayout>
    );
};

export default PortalPayRunDetailPage;
